import { useState } from 'react'
import {
  Box, IconButton, Menu, MenuItem, Stack, Tooltip, Typography,
} from '@mui/material'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import AllInclusiveRoundedIcon from '@mui/icons-material/AllInclusiveRounded'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../lib/api'
import { useCustomerStore } from '../store/customerStore'

interface Props {
  collapsed: boolean
}

/// Active-customer picker for the sidebar. Shows the current selection
/// ("All" or a specific customer), opens a menu of every customer the
/// caller has access to, and on change:
///   1. Writes the choice into the customerStore (which the api-client
///      reads on every subsequent request).
///   2. Invalidates the entire query cache so every list refetches
///      with the new scope. Without this, react-query happily returns
///      the cached data from the previous scope.
///
/// Hidden entirely for users who only have access to one customer —
/// the switcher is just noise in that case.
export function CustomerSwitcher({ collapsed }: Props) {
  const qc = useQueryClient()
  const customerId = useCustomerStore((s) => s.customerId)
  const setCustomer = useCustomerStore((s) => s.setCustomer)
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)

  const customersQ = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.customers.list(),
    staleTime: 5 * 60_000,
  })
  const customers = Array.isArray(customersQ.data) ? customersQ.data : []

  // Single-customer accounts don't need a switcher — keeps the
  // sidebar uncluttered for the most common case (one customer per
  // workspace, no multi-tenancy).
  if (customersQ.isSuccess && customers.length <= 1) return null
  if (customersQ.isLoading) return null

  const selected = customerId ? customers.find((c) => c.id === customerId) : null
  const label = selected?.name ?? 'All customers'

  const handleSelect = (id: string | null) => {
    setCustomer(id)
    setAnchor(null)
    // Drop every cached query so the next render fires fresh requests
    // with the new X-Customer-Id. Cheaper than tracking which queries
    // are customer-scoped and which aren't.
    qc.invalidateQueries()
  }

  return (
    <Box sx={{
      px: collapsed ? 0 : 2.5, pb: 1.25,
      // In slim mode, centre the trigger in the rail so it sits
      // visually under the brand icon. Without this the button left-
      // aligns against the sidebar's content padding and reads as
      // misaligned in the collapsed view.
      ...(collapsed && { display: 'flex', justifyContent: 'center' }),
    }}>
      {collapsed ? (
        <Tooltip title={`Customer: ${label}`} placement="right" arrow>
          <IconButton
            size="small"
            onClick={(e) => setAnchor(e.currentTarget)}
            sx={{
              color: 'text.secondary',
              width: 32, height: 32, borderRadius: 1.5,
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <BusinessOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      ) : (
        <Box
          onClick={(e) => setAnchor(e.currentTarget)}
          role="button"
          aria-label="Switch customer"
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
            borderRadius: 1.5, cursor: 'pointer',
            border: '1px solid', borderColor: 'divider',
            bgcolor: 'background.paper',
            transition: 'border-color 120ms ease, background-color 120ms ease',
            '&:hover': { borderColor: 'primary.main' },
          }}
        >
          <Box sx={{
            width: 26, height: 26, borderRadius: 1, display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center',
            bgcolor: (t) => `${t.palette.primary.main}14`, color: 'primary.main',
            flexShrink: 0,
          }}>
            {selected
              ? <BusinessOutlinedIcon sx={{ fontSize: 16 }} />
              : <AllInclusiveRoundedIcon sx={{ fontSize: 16 }} />}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 10.5, color: 'text.secondary', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Customer
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, letterSpacing: '-0.005em' }} noWrap>
              {label}
            </Typography>
          </Box>
          <KeyboardArrowDownRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        </Box>
      )}

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, minWidth: 240, borderRadius: 2 } } }}
      >
        <MenuItem
          onClick={() => handleSelect(null)}
          sx={{ fontSize: 13.5, fontWeight: 600 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: '100%' }}>
            <AllInclusiveRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Box sx={{ flex: 1 }}>All customers</Box>
            {customerId === null && <CheckRoundedIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
          </Stack>
        </MenuItem>
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', my: 0.5 }} />
        {customers.map((c) => (
          <MenuItem
            key={c.id}
            onClick={() => handleSelect(c.id)}
            sx={{ fontSize: 13.5 }}
          >
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: '100%' }}>
              <BusinessOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600 }} noWrap>{c.name}</Typography>
                {typeof c.site_count === 'number' && (
                  <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                    {c.site_count} site{c.site_count === 1 ? '' : 's'}
                  </Typography>
                )}
              </Box>
              {customerId === c.id && <CheckRoundedIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
            </Stack>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}
